import { buildAuthorizeUrl } from '@/features/auth/oauth-config';

/**
 * CSRF/replay protection + return-path plumbing for the Barita OAuth redirect
 * round trip.
 *
 * `sessionStorage` (not `localStorage`): the pending record is only ever
 * needed by the same tab that started the redirect, and it must not survive
 * longer than that one attempt.
 */
const PENDING_KEY = 'bfmonline.oauth.pending';

/**
 * Ceiling on automatic re-attempts after a failed callback (invalid state,
 * invalid/expired token, an `error` response from Barita, …). Capping this at
 * 1 is what turns "retry once" into "never loop forever" — after the cap is
 * hit, the caller must show the user an explicit retry action instead of
 * auto-redirecting again.
 */
const MAX_AUTO_RETRIES = 1;

export interface PendingAuth {
  /** Random, single-use value echoed back by Barita; must match on return. */
  state: string;
  /** BFM Online path (+ query) to return to once authenticated. */
  returnTo: string;
  /** How many automatic retries have already been spent on this attempt. */
  attempt: number;
}

function generateState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function readPending(): PendingAuth | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingAuth;
  } catch {
    return null;
  }
}

/** Single-use read: returns the pending auth context (if any) and clears it. */
export function consumePendingAuth(): PendingAuth | null {
  const pending = readPending();
  sessionStorage.removeItem(PENDING_KEY);
  return pending;
}

export function clearPendingAuth(): void {
  sessionStorage.removeItem(PENDING_KEY);
}

/** Whether another automatic retry is allowed after a callback failure. */
export function canAutoRetry(attempt: number): boolean {
  return attempt < MAX_AUTO_RETRIES;
}

/**
 * Thin indirection around the actual browser navigation. `window.location`
 * is a non-configurable platform object that (correctly) can't be
 * intercepted with a spy in jsdom, so tests substitute this instead of the
 * real navigation.
 */
export const navigation = {
  redirectTo(url: string): void {
    window.location.assign(url);
  },
};

/**
 * Send the browser to the Barita auth server to (re-)authenticate. Generates
 * a fresh `state`, stores it alongside `returnTo` so the callback can verify
 * the response and land the user back where they started, then navigates
 * away — there is nothing more for the SPA to do until Barita redirects back.
 */
export function redirectToAuthServer(returnTo: string, attempt = 0): void {
  const pending: PendingAuth = { state: generateState(), returnTo, attempt };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  navigation.redirectTo(buildAuthorizeUrl(pending.state));
}
